import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { useJsApiLoader } from "@react-google-maps/api";
import usePlacesAutocomplete, {
  getGeocode,
  getLatLng,
} from "use-places-autocomplete";
import Map from "./Map";
import {
  TextField,
  Container,
  Button,
  Typography,
  Checkbox,
  Box,
  List,
  ListItemButton,
  ListItemText,
  Divider,
  FormControlLabel,
  CircularProgress,
  InputAdornment,
  Chip,
  Stack,
  IconButton,
  ListItem,
  Alert,
} from "@mui/material";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
  glassDropdown,
  glassPanel,
  glassPanelStrong,
  glassTextField,
} from "./glass";
import stripesImg from "./imgs/stripes.jpg";
import axios from "axios";
import { DEFAULT_HOME_HERO } from "./usePublicSiteSettings";
import { estimateLotAreaFromGeocoderResult } from "./mapsLotEstimate";
import "./form.css";

const center = {
  lat: 41.491632,
  lng: -78.677658,
};

function createParcelId() {
  return `parcel-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export const LocationContext = createContext(center);

export const useLocation = () => useContext(LocationContext);

function AddressAutocomplete({
  onPlaceSelected,
  parcels,
  activeParcel,
  activeParcelId,
  setActiveParcelId,
  onRemoveParcel,
  onRequestClearMapOutline,
  selectedServiceIds = [],
  catalogServices = [],
  onRequestQuote,
  contactFirstName,
  setContactFirstName,
  contactLastName,
  setContactLastName,
  contactEmail,
  setContactEmail,
  contactPhone,
  setContactPhone,
  scheduleMonthly,
  setScheduleMonthly,
  quoteSubmit,
}) {
  const {
    ready,
    value,
    suggestions: { status, data },
    setValue,
    clearSuggestions,
  } = usePlacesAutocomplete({
    requestOptions: {},
    debounce: 300,
  });

  const handleInput = (e) => {
    setValue(e.target.value);
  };

  const findperimeter = (areaSqft) => {
    const pi = Math.PI;
    const radius = Math.sqrt(areaSqft / pi);
    return Math.round(2 * pi * radius);
  };

  const handleSelect =
    ({ description }) =>
    () => {
      clearSuggestions();

      getGeocode({ address: description })
        .then((results) => {
          const first = results[0];
          if (!first) {
            throw new Error("No geocode results");
          }
          return getLatLng(first).then((ll) => ({ ll, first }));
        })
        .then(({ ll, first }) => {
          const est = estimateLotAreaFromGeocoderResult(first);
          let acres = null;
          let yardEstimateStatus = "no_data";
          let geocoderSnapshot = null;
          if (est && est.sqft > 0) {
            const payload = {
              Acres: est.sqft,
              mapsLocationType: est.locationType,
              mapsMethod: est.method,
              mapsNote: est.note,
              source: "geocoder",
            };
            geocoderSnapshot = payload;
            acres = payload;
            yardEstimateStatus = "found";
          }
          onPlaceSelected({
            address: description,
            location: ll,
            acres,
            yardEstimateStatus,
            geocoderSnapshot,
            outlinePath: null,
          });
          setValue("", false);
        })
        .catch((error) => {
          console.log("Geocode error: ", error);
          setValue(description, false);
        });
    };

  const showSuggestions = status === "OK" && data.length > 0;
  const highlightedParcelId = activeParcelId ?? parcels[0]?.id ?? null;

  return (
    <>
      <Box sx={{ position: "relative", width: "100%", mt: 1 }}>
        <TextField
          value={value}
          onChange={handleInput}
          disabled={!ready}
          placeholder="Search your address"
          fullWidth
          autoComplete="off"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <LocationOnOutlinedIcon
                  sx={{ color: "text.secondary", opacity: 0.85 }}
                />
              </InputAdornment>
            ),
          }}
          helperText={
            ready
              ? "Pick a suggestion to add a property. Repeat to include more lots in the same quote."
              : "Loading address search…"
          }
          FormHelperTextProps={{
            sx: { mx: 0, mt: 1, color: "text.secondary" },
          }}
          sx={{
            ...glassTextField,
            "& .MuiInputBase-input": { py: 1.5 },
          }}
        />

        {showSuggestions && (
          <Box
            component="ul"
            role="listbox"
            aria-label="Address suggestions"
            sx={{
              position: "absolute",
              left: 0,
              right: 0,
              top: "100%",
              zIndex: 1300,
              listStyle: "none",
              m: 0,
              p: 0,
              ...glassDropdown,
              borderRadius: 0,
            }}
          >
            <List disablePadding sx={{ py: 0.5 }}>
              {data.map((suggestion, index) => {
                const {
                  place_id,
                  structured_formatting: { main_text, secondary_text },
                } = suggestion;
                return (
                  <React.Fragment key={place_id}>
                    {index > 0 ? (
                      <Divider sx={{ borderColor: "rgba(0,0,0,0.08)" }} />
                    ) : null}
                    <ListItemButton
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={handleSelect(suggestion)}
                      sx={{
                        alignItems: "flex-start",
                        py: 1.25,
                        px: 2,
                        borderRadius: 0,
                        mx: 0,
                        my: 0,
                        transition: "background-color 0.15s ease",
                        "&:hover": {
                          backgroundColor: "rgba(45, 106, 79, 0.08)",
                        },
                      }}
                    >
                      <ListItemText
                        primary={main_text}
                        secondary={secondary_text}
                        primaryTypographyProps={{
                          fontWeight: 600,
                          letterSpacing: "-0.02em",
                          sx: {
                            whiteSpace: "normal",
                            wordBreak: "break-word",
                            lineHeight: 1.35,
                          },
                        }}
                        secondaryTypographyProps={{
                          variant: "body2",
                          component: "span",
                          sx: {
                            opacity: 0.75,
                            mt: 0.5,
                            display: "block",
                            whiteSpace: "normal",
                            wordBreak: "break-word",
                            lineHeight: 1.4,
                          },
                        }}
                      />
                    </ListItemButton>
                  </React.Fragment>
                );
              })}
            </List>
          </Box>
        )}
      </Box>

      {parcels.length > 0 && activeParcel ? (
        <>
          <Box sx={{ ...glassPanel, borderRadius: 3, p: 2, mb: 2, mt: 2 }}>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              Properties in this quote ({parcels.length})
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5, lineHeight: 1.45 }}>
              The highlighted row matches the map at the top of the page. Tap a row to switch lots; use{" "}
              <strong>remove</strong> to drop it from the request.
            </Typography>
            <List disablePadding>
              {parcels.map((p) => (
                <ListItem
                  key={p.id}
                  disablePadding
                  secondaryAction={
                    <IconButton
                      edge="end"
                      aria-label={`Remove ${p.address}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        onRemoveParcel(p.id);
                      }}
                    >
                      <DeleteOutlineIcon />
                    </IconButton>
                  }
                  sx={{
                    mb: 1,
                    border: "2px solid",
                    borderColor: p.id === highlightedParcelId ? "primary.main" : "rgba(0,0,0,0.08)",
                    borderRadius: 2,
                    bgcolor: p.id === highlightedParcelId ? "rgba(45, 106, 79, 0.06)" : "transparent",
                  }}
                >
                  <ListItemButton
                    onClick={() => setActiveParcelId(p.id)}
                    selected={p.id === highlightedParcelId}
                    sx={{ borderRadius: 1.5, pr: 6 }}
                  >
                    <ListItemText
                      primary={p.address}
                      secondary={
                        p.acres?.Acres
                          ? `${p.acres.source === "polygon" ? "Outlined" : "Estimate"}: ${p.acres.Acres.toLocaleString()} sq ft`
                          : "No size estimate yet"
                      }
                      primaryTypographyProps={{ fontWeight: 600, sx: { lineHeight: 1.35 } }}
                      secondaryTypographyProps={{ variant: "caption" }}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Box>

          <Box
            sx={{
              mt: 2,
              pb: 4,
            }}
          >
            <Box sx={{ ...glassPanel, borderRadius: 3, p: 2.5, mb: 2 }}>
              <Typography variant="overline" color="text.secondary">
                Property on map
              </Typography>
              <Typography variant="body1" fontWeight={600} sx={{ mt: 0.5 }}>
                {activeParcel.address}
              </Typography>
            </Box>

            <Box sx={{ ...glassPanel, borderRadius: 3, p: 2.5, mb: 2 }}>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                Services requested
              </Typography>
              {selectedServiceIds.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                  Tap cards in <strong>Local services</strong> (below) to add mowing, trimming, fencing, and
                  more to your quote.
                </Typography>
              ) : (
                <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mt: 0.5 }}>
                  {catalogServices.filter((s) => selectedServiceIds.includes(s.id)).map((s) => (
                    <Chip
                      key={s.id}
                      label={`${s.name} · ${s.price}`}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  ))}
                </Stack>
              )}
            </Box>

            <Box sx={{ ...glassPanel, borderRadius: 3, p: 2.5, mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom fontWeight={600}>
                Your details
              </Typography>
              <TextField
                label="First name"
                fullWidth
                required
                value={contactFirstName}
                onChange={(e) => setContactFirstName(e.target.value)}
                sx={{ mt: 1, ...glassTextField }}
              />
              <TextField
                label="Last name"
                fullWidth
                value={contactLastName}
                onChange={(e) => setContactLastName(e.target.value)}
                sx={{ mt: 1.5, ...glassTextField }}
              />
              <TextField
                label="Email"
                type="email"
                fullWidth
                required
                value={contactEmail}
                onChange={(e) => setContactEmail(e.target.value)}
                sx={{ mt: 1.5, ...glassTextField }}
              />
              <TextField
                label="Phone"
                type="tel"
                fullWidth
                value={contactPhone}
                onChange={(e) => setContactPhone(e.target.value)}
                sx={{ mt: 1.5, ...glassTextField }}
              />
            </Box>

            <Box sx={{ ...glassPanelStrong, borderRadius: 3, p: 2.5, mb: 2 }}>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                Yard estimate
              </Typography>
              {activeParcel.acres?.source === "polygon" ? (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                  <strong>Drawn on the map</strong> — use the lawnmower brush on the satellite map, then drag
                  corners to adjust. Area uses Google&apos;s spherical geometry (good for quotes, not a legal
                  survey).
                </Typography>
              ) : (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                  <strong>Optional map outline:</strong> scroll up and use the paintbrush on the satellite map
                  for a tighter quote. Otherwise we use a geocoder-based estimate below.
                </Typography>
              )}
              {activeParcel.yardEstimateStatus === "found" && activeParcel.acres ? (
                <>
                  <Typography variant="body1" sx={{ mt: 1 }}>
                    {activeParcel.acres.source === "polygon" ? "Outlined area: " : "Working estimate: "}
                    <strong>{activeParcel.acres.Acres.toLocaleString()}</strong> sq ft.
                  </Typography>
                  {activeParcel.acres.source === "polygon" ? (
                    <Button
                      size="small"
                      variant="outlined"
                      sx={{ mt: 1.5 }}
                      onClick={() => onRequestClearMapOutline?.()}
                    >
                      Clear outline &amp; use map estimate
                    </Button>
                  ) : null}
                  {activeParcel.acres.source !== "polygon" && activeParcel.acres.mapsLocationType ? (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                      Geocoder precision: <strong>{activeParcel.acres.mapsLocationType}</strong>
                      {activeParcel.acres.mapsMethod ? ` · method: ${activeParcel.acres.mapsMethod}` : ""}
                    </Typography>
                  ) : null}
                  {activeParcel.acres.source !== "polygon" && activeParcel.acres.mapsNote ? (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.75 }}>
                      {activeParcel.acres.mapsNote}
                    </Typography>
                  ) : null}
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    If this area were a circle, that&apos;s about{" "}
                    <strong>{findperimeter(activeParcel.acres.Acres)}</strong> ft around the edge (illustration
                    only).
                  </Typography>
                </>
              ) : null}
              {activeParcel.yardEstimateStatus === "no_data" ? (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  No automatic estimate — use the <strong>paintbrush</strong> on the satellite map above to trace
                  your lawn, or pick a more specific address.
                </Typography>
              ) : null}
            </Box>

            <Box
              sx={{
                ...glassPanel,
                borderRadius: 3,
                p: 2,
                mb: 0,
                display: "flex",
                flexDirection: "column",
                gap: 1,
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    color="primary"
                    checked={scheduleMonthly}
                    onChange={(e) => setScheduleMonthly(e.target.checked)}
                  />
                }
                label="Schedule monthly — cancel anytime"
              />
            </Box>
          </Box>

          <Box
            sx={{
              position: "fixed",
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 1095,
              px: 2,
              py: 1.5,
              pb: "max(12px, env(safe-area-inset-bottom, 0px))",
              background: "rgba(255, 255, 255, 0.88)",
              backdropFilter: "blur(28px) saturate(190%)",
              WebkitBackdropFilter: "blur(28px) saturate(190%)",
              borderTop: "1px solid rgba(255, 255, 255, 0.75)",
              boxShadow: "0 -8px 32px rgba(0, 0, 0, 0.08)",
            }}
          >
            <Box sx={{ maxWidth: 600, mx: "auto", width: "100%" }}>
              {quoteSubmit.status === "success" ? (
                <Alert severity="success" sx={{ mb: 1.5, borderRadius: 2 }}>
                  {quoteSubmit.message}
                </Alert>
              ) : null}
              {quoteSubmit.status === "error" ? (
                <Alert severity="error" sx={{ mb: 1.5, borderRadius: 2 }}>
                  {quoteSubmit.message}
                </Alert>
              ) : null}
              <Button
                variant="contained"
                color="primary"
                size="large"
                fullWidth
                disabled={
                  selectedServiceIds.length === 0 ||
                  parcels.length === 0 ||
                  quoteSubmit.status === "loading"
                }
                onClick={onRequestQuote}
              >
                {quoteSubmit.status === "loading" ? "Sending…" : "Fast quote"}
              </Button>
              {selectedServiceIds.length === 0 || parcels.length === 0 ? (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 1, textAlign: "center", display: "block", lineHeight: 1.4 }}
                >
                  {parcels.length === 0
                    ? "Add at least one property using the address search above."
                    : "Choose at least one service in Local services below."}
                </Typography>
              ) : null}
            </Box>
          </Box>
        </>
      ) : null}
    </>
  );
}

const Searchbar = ({ selectedServiceIds = [], catalogServices = [], homeHero, operatorQuoteSlug = "" }) => {
  const hero =
    homeHero && typeof homeHero === "object"
      ? { ...DEFAULT_HOME_HERO, ...homeHero }
      : DEFAULT_HOME_HERO;
  const heroBgUrl =
    hero.imageUrl && String(hero.imageUrl).trim()
      ? String(hero.imageUrl).trim()
      : stripesImg;
  const heroBackgroundImage = `linear-gradient(180deg, rgba(0,0,0,0.25) 0%, rgba(0,0,0,0.45) 100%), url(${heroBgUrl})`;

  const googleMapsApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";
  const { isLoaded, loadError } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey,
    libraries: ["places", "geometry"],
  });

  const [parcels, setParcels] = useState([]);
  const [activeParcelId, setActiveParcelId] = useState(null);
  const [drawResetById, setDrawResetById] = useState({});
  const [contactFirstName, setContactFirstName] = useState("");
  const [contactLastName, setContactLastName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [scheduleMonthly, setScheduleMonthly] = useState(false);
  const [quoteSubmit, setQuoteSubmit] = useState({ status: "idle", message: "" });

  const activeParcel =
    parcels.find((p) => p.id === activeParcelId) ?? parcels[0] ?? null;

  useEffect(() => {
    if (parcels.length === 0) {
      setActiveParcelId(null);
      return;
    }
    if (!activeParcelId || !parcels.some((p) => p.id === activeParcelId)) {
      setActiveParcelId(parcels[0].id);
    }
  }, [parcels, activeParcelId]);

  const handlePlaceSelected = useCallback((data) => {
    const id = createParcelId();
    setParcels((prev) => [...prev, { id, ...data }]);
    setActiveParcelId(id);
  }, []);

  const handleRemoveParcel = useCallback((id) => {
    setParcels((prev) => prev.filter((p) => p.id !== id));
  }, []);

  const handleDrawnAreaChange = useCallback((sqft, meta) => {
    if (!activeParcelId) return;
    setParcels((list) =>
      list.map((p) => {
        if (p.id !== activeParcelId) return p;
        if (sqft == null) {
          return {
            ...p,
            outlinePath: meta?.path === null ? null : p.outlinePath,
            acres: p.geocoderSnapshot,
            yardEstimateStatus: p.geocoderSnapshot ? "found" : "no_data",
          };
        }
        return {
          ...p,
          acres: { Acres: sqft, source: "polygon" },
          yardEstimateStatus: "found",
          outlinePath: meta?.path ?? p.outlinePath,
        };
      })
    );
  }, [activeParcelId]);

  const handleClearMapOutline = useCallback(() => {
    if (!activeParcelId) return;
    setParcels((list) =>
      list.map((p) =>
        p.id !== activeParcelId
          ? p
          : {
              ...p,
              outlinePath: null,
              acres: p.geocoderSnapshot,
              yardEstimateStatus: p.geocoderSnapshot ? "found" : "no_data",
            }
      )
    );
    setDrawResetById((prev) => ({
      ...prev,
      [activeParcelId]: (prev[activeParcelId] || 0) + 1,
    }));
  }, [activeParcelId]);

  const handleRequestQuote = useCallback(async () => {
    const first = contactFirstName.trim();
    const em = contactEmail.trim();
    if (!first || !em) {
      setQuoteSubmit({
        status: "error",
        message: "Please enter your first name and email before requesting a quote.",
      });
      return;
    }
    setQuoteSubmit({ status: "loading", message: "" });
    const services = catalogServices
      .filter((s) => selectedServiceIds.includes(s.id))
      .map((s) => ({
        id: s.id,
        name: s.name,
        price: s.price,
      }));
    const payload = {
      contact: {
        firstName: contactFirstName.trim(),
        lastName: contactLastName.trim(),
        email: em,
        phone: contactPhone.trim(),
      },
      scheduleMonthly,
      parcels: parcels.map((p) => ({
        id: p.id,
        address: p.address,
        location: p.location,
        areaSqFt: p.acres?.Acres ?? null,
        areaSource: p.acres?.source ?? null,
        mapsLocationType: p.acres?.mapsLocationType ?? null,
        mapsMethod: p.acres?.mapsMethod ?? null,
        mapsNote: p.acres?.mapsNote ?? null,
        outlinePath: p.outlinePath ?? null,
      })),
      serviceIds: selectedServiceIds,
      services,
      operatorQuoteSlug: (operatorQuoteSlug || "").trim().toLowerCase(),
    };
    try {
      await axios.post("/quote", payload);
      setQuoteSubmit({
        status: "success",
        message: "Your quote request was sent. We'll get back to you soon.",
      });
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        err.message ||
        "Could not send your quote. Please try again later.";
      setQuoteSubmit({ status: "error", message: msg });
    }
  }, [
    parcels,
    selectedServiceIds,
    contactFirstName,
    contactLastName,
    contactEmail,
    contactPhone,
    scheduleMonthly,
    catalogServices,
    operatorQuoteSlug,
  ]);

  const mapsError =
    loadError ||
    (!googleMapsApiKey && "Add REACT_APP_GOOGLE_MAPS_API_KEY to .env.local");

  const contextLocation = activeParcel?.location ?? center;

  return (
    <LocationContext.Provider value={contextLocation}>
      <Box>
        {activeParcel?.location ? (
          <Box
            sx={{
              ...glassPanelStrong,
              borderRadius: 3,
              overflow: "hidden",
              m: 2,
              boxShadow: "0 16px 48px rgba(0,0,0,0.12)",
            }}
          >
            <Typography
              variant="caption"
              display="block"
              sx={{ px: 1.5, pt: 1, pb: 0.5, color: "text.secondary" }}
            >
              Lawn brush applies to <strong>{activeParcel.address}</strong>. Switch properties in the list
              below to outline another lot.
            </Typography>
            <Map
              key={`${activeParcel.id}-${drawResetById[activeParcel.id] || 0}`}
              location={activeParcel.location}
              savedOutlinePath={activeParcel.outlinePath}
              onDrawnAreaChange={handleDrawnAreaChange}
              resetDrawingSignal={drawResetById[activeParcel.id] || 0}
            />
          </Box>
        ) : (
          <Box
            className="mow-hero"
            sx={{
              position: "relative",
              width: "100%",
              minHeight: { xs: "36vh", sm: "40vh" },
              backgroundImage: heroBackgroundImage,
              backgroundSize: "cover",
              backgroundPosition: "center",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              px: 2,
              pt: 7,
              pb: 2,
            }}
          >
            <Box
              sx={{
                ...glassPanelStrong,
                borderRadius: 4,
                px: 3,
                py: 2.5,
                maxWidth: 420,
                textAlign: "center",
              }}
            >
              <Typography
                variant="h5"
                sx={{
                  color: "text.primary",
                  fontWeight: 700,
                  letterSpacing: "-0.03em",
                  lineHeight: 1.2,
                }}
              >
                {hero.headline}
              </Typography>
              <Typography
                variant="body2"
                sx={{ mt: 1.5, color: "text.secondary", lineHeight: 1.5 }}
              >
                {hero.subheadline}
              </Typography>
            </Box>
          </Box>
        )}
      </Box>

      <Container maxWidth="sm" sx={{ pt: 2, px: 2, pb: 0.5 }}>
        {mapsError ? (
          <Typography color="error" sx={{ mt: 2 }}>
            {typeof mapsError === "string"
              ? mapsError
              : "Google Maps could not load. Check the API key and billing."}
          </Typography>
        ) : null}

        {!mapsError && !isLoaded ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 3 }}>
            <CircularProgress size={22} thickness={5} />
            <Typography color="text.secondary">Loading maps…</Typography>
          </Box>
        ) : null}

        {isLoaded && !loadError && googleMapsApiKey ? (
          <AddressAutocomplete
            onPlaceSelected={handlePlaceSelected}
            parcels={parcels}
            activeParcel={activeParcel}
            activeParcelId={activeParcelId}
            setActiveParcelId={setActiveParcelId}
            onRemoveParcel={handleRemoveParcel}
            onRequestClearMapOutline={handleClearMapOutline}
            selectedServiceIds={selectedServiceIds}
            catalogServices={catalogServices}
            onRequestQuote={handleRequestQuote}
            contactFirstName={contactFirstName}
            setContactFirstName={setContactFirstName}
            contactLastName={contactLastName}
            setContactLastName={setContactLastName}
            contactEmail={contactEmail}
            setContactEmail={setContactEmail}
            contactPhone={contactPhone}
            setContactPhone={setContactPhone}
            scheduleMonthly={scheduleMonthly}
            setScheduleMonthly={setScheduleMonthly}
            quoteSubmit={quoteSubmit}
          />
        ) : null}
      </Container>
    </LocationContext.Provider>
  );
};

export default Searchbar;
